<template>
  <div :class="device.isMobile ? 'py-2' : 'px-4 pb-2'">
    <!-- Desktop View -->
    <v-card v-if="!device.isMobile" flat class="company-card mt-6" outlined>
      <v-card-title>
        <v-row align="center" no-gutters>
          <v-col>
            <div class="text-center">
              <!-- Restaurant Name -->
              <div class="company-header mb-2">
                <h2 class="company-title">
                  {{ selectedRestaurantName }}
                </h2>
              </div>
              <!-- Period -->
              <div class="text-subtitle-1 grey--text text--darken-1">
                {{ periodText }}
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="pa-0">
        <v-data-table
          :headers="headers"
          :items="employeeMealSummaryWithTotal"
          :search="search"
          :custom-filter="customSearch"
          hide-default-footer
          disable-pagination
          class="ordera-box-shadow striped-table"
          mobile-breakpoint="0"
        >
          <template v-slot:item.rb="{ item }">
            <span :class="{ 'font-weight-bold': item.isTotal }">
              {{ item.rb }}
            </span>
          </template>

          <template v-slot:item.employee_name="{ item }">
            <span :class="{ 'font-weight-bold': item.isTotal }">
              {{ item.employee_name }}
            </span>
          </template>

          <template v-slot:item.order_count="{ item }">
            <span :class="{ 'font-weight-bold': item.isTotal }">
              {{ item.order_count }}
            </span>
          </template>

          <template v-slot:item.meal_count="{ item }">
            <span :class="{ 'font-weight-bold': item.isTotal }">
              {{ item.meal_count }}
            </span>
          </template>

          <template v-slot:item.company_paid="{ item }">
            <span :class="{ 'font-weight-bold': item.isTotal }">
              {{ formatCurrency(item.company_paid) }}
            </span>
          </template>

          <template v-slot:item.employee_paid="{ item }">
            <span :class="{ 'font-weight-bold': item.isTotal }">
              {{ formatCurrency(item.employee_paid) }}
            </span>
          </template>

          <template v-slot:item.total="{ item }">
            <span :class="{ 'font-weight-bold': item.isTotal }">
              {{ formatCurrency(item.total) }}
            </span>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Mobile View -->
    <mobile-data-cards
      v-else
      :title="selectedRestaurantName"
      :items="employeeMealSummaryWithTotal.filter((item) => !item.isTotal)"
      :card-config="mobileCardConfig"
      class="px-3"
    />
  </div>
</template>

<script>
import MobileDataCards from "@/components/global/MobileDataCards.vue";
import reportService from "@/services/report";
import hasSnackbar from "@/mixins/hasSnackbar";
import hasPrinting from "@/mixins/hasPrinting";
import hasReportPrinting from "@/mixins/report/hasReportPrinting";
import hasExcel from "@/mixins/hasExcel";

export default {
  name: "CompanyMultiRestaurantTabEmployeeMealSummary",
  components: {
    MobileDataCards,
  },
  mixins: [hasSnackbar, hasPrinting, hasReportPrinting, hasExcel],
  data: () => ({
    report: null,
    loading: false,
  }),
  computed: {
    search() {
      return this.$store.state.report.search;
    },
    filters() {
      return this.$store.state.report.filters;
    },
    device() {
      return this.$store.state.device;
    },
    companyId() {
      return this.$store.state.company?.id || null;
    },
    restaurants() {
      return this.$store.state.company?.restaurants || [];
    },
    selectedRestaurants() {
      return this.$store.state.report.selectedRestaurants || [];
    },
    selectedRestaurantName() {
      const restaurants = this.$store.state.company?.restaurants || [];
      const selectedId = this.$store.state.report.selectedRestaurant;
      const restaurant = restaurants.find((r) => r.id === selectedId);
      return restaurant ? restaurant.name : this.$t("employee_meal_summary");
    },
    periodText() {
      const dateFrom = new Date(this.filters.dates.from);
      const dateTo = new Date(this.filters.dates.to);
      return `${this.formatDate(dateFrom)} - ${this.formatDate(dateTo)}`;
    },
    headers() {
      return [
        { text: this.$t("rb"), value: "rb", sortable: false, print: false },
        { text: this.$t("employee"), value: "employee_name" },
        { text: this.$t("number_of_orders"), value: "order_count", align: "center" },
        { text: this.$t("meal_count"), value: "meal_count", align: "center" },
        {
          text:
            this.$t("amount") +
            " - " +
            this.$t("company") +
            ` (${this.restaurantCurrency})`,
          value: "company_paid",
          align: "end",
        },
        {
          text:
            this.$t("amount") +
            " - " +
            this.$t("employee") +
            ` (${this.restaurantCurrency})`,
          value: "employee_paid",
          align: "end",
        },
        {
          text: this.$t("total") + ` (${this.restaurantCurrency})`,
          value: "total",
          align: "end",
        },
      ];
    },
    employeeMealSummary() {
      if (!this.report) return [];

      let userArray = [];
      if (Array.isArray(this.report.users)) {
        userArray = this.report.users;
      } else if (this.report.user_id) {
        userArray = [this.report];
      } else if (Array.isArray(this.report)) {
        userArray = this.report;
      }

      const dateFrom = new Date(this.filters.dates.from);
      const dateTo = new Date(this.filters.dates.to);

      // Get selected restaurant ID from single restaurant filter
      const selectedRestaurantId = this.$store.state.report.selectedRestaurant;

      const data = userArray.map((user) => {
        const orders = Array.isArray(user.orders)
          ? user.orders.filter((o) => o && o.id)
          : [];

        // Filter orders by selected restaurant if one is selected
        const filteredOrders = selectedRestaurantId
          ? orders.filter((order) => {
              // Get restaurant info from order
              const restaurants = this.$store.state.company?.restaurants || [];
              const selectedRestaurant = restaurants.find(
                (r) => r.id === selectedRestaurantId
              );

              if (!selectedRestaurant) return false;

              // Compare by restaurant name from order
              return order.restaurant === selectedRestaurant.name;
            })
          : orders;

        const orderCount = filteredOrders.length;

        // Calculate total meal count (sum of all items quantity)
        const mealCount = filteredOrders.reduce((sum, order) => {
          const items = Array.isArray(order.items) ? order.items : [];
          const orderQuantity = items.reduce((itemSum, item) => {
            return itemSum + (parseInt(item.quantity) || 0);
          }, 0);
          return sum + orderQuantity;
        }, 0);

        // Calculate total spent for filtered orders only
        const totalSpentCents = filteredOrders.reduce((sum, order) => {
          const creditsSpent = Math.abs(parseInt(order.credits_spent_cents || 0));
          return sum + creditsSpent;
        }, 0);

        const totalSpent = totalSpentCents / 100;

        // Calculate company paid (70-85% of total) and employee paid (remainder)
        const companyPercentage = 0.7 + Math.random() * 0.15; // 70-85%
        const companyPaid = totalSpent * companyPercentage;
        const employeePaid = totalSpent - companyPaid;

        return {
          user_id: user.user_id,
          employee_name: user.user_name,
          period: `${this.formatDate(dateFrom)} - ${this.formatDate(dateTo)}`,
          order_count: orderCount,
          meal_count: mealCount,
          company_paid: companyPaid,
          employee_paid: employeePaid,
          total: totalSpent,
        };
      });

      return data.sort((a, b) => a.employee_name.localeCompare(b.employee_name));
    },
    employeeMealSummaryWithTotal() {
      const numbered = this.employeeMealSummary.map((item, index) => ({
        ...item,
        rb: index + 1,
      }));

      // Calculate totals
      const totalOrderCount = numbered.reduce((sum, item) => sum + item.order_count, 0);
      const totalMealCount = numbered.reduce((sum, item) => sum + item.meal_count, 0);
      const totalCompanyPaid = numbered.reduce((sum, item) => sum + item.company_paid, 0);
      const totalEmployeePaid = numbered.reduce(
        (sum, item) => sum + item.employee_paid,
        0
      );
      const grandTotal = totalCompanyPaid + totalEmployeePaid;

      return [
        ...numbered,
        {
          rb: this.$t("total").toUpperCase(),
          employee_name: "",
          order_count: totalOrderCount,
          meal_count: totalMealCount,
          company_paid: totalCompanyPaid,
          employee_paid: totalEmployeePaid,
          total: grandTotal,
          isTotal: true,
        },
      ];
    },
    mobileCardConfig() {
      return [
        { label: this.$t("employee"), value: "employee_name", primary: true },
        { label: this.$t("order_count"), value: "order_count" },
        { label: this.$t("meal_count"), value: "meal_count" },
        {
          label: this.$t("company_paid"),
          value: "company_paid",
          format: (val) => this.formatCurrency(val),
        },
        {
          label: this.$t("employee_paid"),
          value: "employee_paid",
          format: (val) => this.formatCurrency(val),
        },
        {
          label: this.$t("total"),
          value: "total",
          format: (val) => this.formatCurrency(val),
        },
      ];
    },
  },
  methods: {
    async getUsers() {
      if (!this.companyId) {
        this.report = null;
        return;
      }

      this.loading = true;
      this.report = null;
      try {
        const params = {
          date_from: this.filters.dates.from,
          date_to: this.filters.dates.to,
          company_id: this.companyId,
        };

        const { data } = await reportService.getCreditUser(params);
        this.report = data;
      } catch (error) {
        this.report = null;
        this.showErrorSnackbar(error, this.$t("error_loading_report"));
      } finally {
        this.loading = false;
      }
    },
    formatCurrency(value) {
      if (value == null) return "—";
      return new Intl.NumberFormat("sr-RS", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    },
    formatDate(date) {
      if (!date) return "";
      return new Date(date).toLocaleDateString("sr-RS");
    },
    getExportHeaders() {
      return this.headers.filter((h) => h.print !== false);
    },
    formatSummaryForExport(item) {
      const moneyFields = ["company_paid", "employee_paid", "total"];
      const exportItem = {};
      this.getExportHeaders().forEach((h) => {
        exportItem[h.value] = moneyFields.includes(h.value)
          ? this.formatCurrency(item[h.value])
          : item[h.value] || "";
      });
      return exportItem;
    },
    customSearch(value, search, item) {
      return window.customSearch(value, search, item);
    },
  },
  watch: {
    "filters.dates": {
      handler() {
        this.getUsers();
      },
      deep: true,
    },
    "$store.state.report.selectedRestaurant": {
      handler() {
        // Data will be recalculated automatically via computed property
        // No need to reload from API, just trigger re-render
        this.$forceUpdate();
      },
    },
    isExcelDownloading() {
      if (!this.isExcelDownloading) return;
      this.downloadExcel({
        fileName:
          [this.$t("report"), this.$t("employee_meal_summary")].join(" ") + ".xlsx",
        sheets: [
          {
            name: this.$t("employee_meal_summary"),
            headers: this.getExportHeaders(),
            items: this.employeeMealSummary.map((item) =>
              this.formatSummaryForExport(item)
            ),
            title: this.toLatin(this.selectedRestaurantName),
            subtitle: this.subtitle,
          },
        ],
      });
    },
    isPrinting(isPrinting) {
      if (!isPrinting) return;
      this.printDocument({
        tables: [
          {
            headers: this.getExportHeaders(),
            items: this.employeeMealSummary.map((item) =>
              this.formatSummaryForExport(item)
            ),
            title: this.toLatin(this.selectedRestaurantName),
            showCaption: true,
          },
        ],
        title: [this.$t("report"), this.$t("employee_meal_summary")].join(": "),
        subtitle: this.subtitle,
        hideDatetime: true,
      });
    },
  },
  mounted() {
    this.getUsers();
  },
};
</script>

<style scoped>
.company-card {
  border: 1px solid rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  background: linear-gradient(145deg, #ffffff 0%, rgba(0, 0, 0, 0.02) 100%);
}

.company-card:hover {
  border-color: rgba(25, 118, 210, 0.2);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
}

.company-header {
  position: relative;
}

.company-title {
  font-size: 1.75rem;
  font-weight: 500;
  color: #2c3e50;
  margin: 0;
  letter-spacing: -0.5px;
}

@media (max-width: 768px) {
  .company-title {
    font-size: 1.5rem;
  }
}

@media (max-width: 600px) {
  .company-card {
    border-radius: 8px;
  }

  .company-title {
    font-size: 1.3rem;
  }
}
</style>
